import React, { useEffect, useState } from 'react';

// Shown while the Ubuntu server is being woken from sleep, between submitting
// login credentials and actually reaching the real backend. Matches the visual
// language established in LoginForm.jsx / AetherisSplashAuth.jsx (Orbitron +
// Rajdhani, purple/blue neon glow, scanlines) rather than introducing a new style.
//
// statusText is a short caller-supplied line ("BOOTING CORE...", "STILL WAKING...",
// "TAKING LONGER THAN USUAL...") so LoginForm can drive the copy based on elapsed
// time without this component knowing about polling/timeouts itself.
const ServerWakingScreen = ({ statusText = 'WAKING SERVER...' }) => {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setDotCount(c => (c + 1) % 4), 450);
    return () => clearInterval(id);
  }, []);

  const dots = '.'.repeat(dotCount);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;600;700&display=swap');

        @keyframes waking-scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }

        @keyframes waking-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 20px rgba(176,38,255,0.6), 0 0 60px rgba(100,180,255,0.3); }
          50% { opacity: 0.55; box-shadow: 0 0 8px rgba(176,38,255,0.3), 0 0 20px rgba(100,180,255,0.15); }
        }

        @keyframes waking-spin {
          to { transform: rotate(360deg); }
        }

        .waking-root {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: radial-gradient(ellipse at center, #140a24 0%, #05040f 70%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-family: 'Rajdhani', sans-serif;
          overflow: hidden;
        }

        .waking-scanline-overlay {
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            0deg,
            rgba(255,255,255,0.02) 0px,
            rgba(255,255,255,0.02) 1px,
            transparent 1px,
            transparent 3px
          );
          pointer-events: none;
        }

        .waking-scanline-beam {
          position: absolute;
          left: 0; right: 0;
          height: 120px;
          background: linear-gradient(180deg, transparent, rgba(100,180,255,0.06), transparent);
          animation: waking-scanline 3.5s linear infinite;
          pointer-events: none;
        }

        .waking-ring {
          width: 84px;
          height: 84px;
          border-radius: 50%;
          border: 2.5px solid rgba(176,38,255,0.25);
          border-top-color: rgba(176,38,255,0.95);
          border-right-color: rgba(100,180,255,0.7);
          animation: waking-spin 1.1s linear infinite, waking-pulse 2.2s ease-in-out infinite;
          margin-bottom: 36px;
        }

        .waking-title {
          font-family: 'Orbitron', monospace;
          font-size: clamp(24px, 5vw, 38px);
          font-weight: 900;
          letter-spacing: 0.2em;
          color: #fff;
          text-shadow:
            0 0 20px rgba(176,38,255,0.6),
            0 0 60px rgba(100,180,255,0.3);
          text-align: center;
          margin-bottom: 14px;
        }

        .waking-status {
          font-family: 'Orbitron', monospace;
          font-size: 12px;
          letter-spacing: 0.15em;
          color: rgba(100,200,255,0.85);
          text-align: center;
          min-height: 18px;
        }

        .waking-hint {
          margin-top: 28px;
          font-family: 'Rajdhani', sans-serif;
          font-size: 13px;
          letter-spacing: 0.04em;
          color: rgba(255,255,255,0.35);
          text-align: center;
          max-width: 340px;
          padding: 0 20px;
        }
      `}</style>

      <div className="waking-root">
        <div className="waking-scanline-overlay" />
        <div className="waking-scanline-beam" />
        <div className="waking-ring" />
        <div className="waking-title">AETHERIS</div>
        <div className="waking-status">{statusText}{dots}</div>
        <div className="waking-hint">
          The server was asleep to save power. It's booting up now — this usually takes under a minute.
        </div>
      </div>
    </>
  );
};

export default ServerWakingScreen;
